/**
 * Stappen van de productconfigurator.
 *
 * Vroeger: configureren, uploaden/ontwerpen, en dan een aparte derde stap met
 * alleen de prijs en een knop "Toevoegen aan winkelwagen". Die derde stap liet
 * niets nieuws zien. Nu staat de winkelwagenknop direct in de ontwerpstap,
 * zodra het ontwerp klaar is. Producten zonder drukbestand gaan vanaf het
 * configureren meteen naar de winkelwagen.
 *
 * De stap 'checkout' blijft bestaan voor beheer (orderpagina, ontwerp aanpassen
 * vanuit de winkelwagen), die daar met een bestaand bestand direct op landt.
 */
export type ConfiguratorStep = 'configure' | 'upload' | 'checkout';

/**
 * `in-designer`: de ingebedde Vinyl Lettering Designer heeft een eigen
 * bestelknop en is dan de enige bestelknop (één bestelknop op de pagina). De
 * configurator toont zelf geen winkelwagenknop zolang de designer actief is.
 */
export type ConfiguratorPrimaryAction = 'go-to-design' | 'add-to-cart' | 'await-design' | 'in-designer';

export function resolveConfiguratorPrimaryAction({
  step,
  isPrintable,
  designsReady,
  designMadeInline = false,
  designerOwnsOrder = false,
}: {
  step: ConfiguratorStep;
  isPrintable: boolean;
  designsReady: boolean;
  /** Ontwerp ontstaat al in de configuratiestap (vinyl-ontwerptool). */
  designMadeInline?: boolean;
  /** De ingebedde designer bestelt zelf (eigen knop); de configurator toont dan geen bestelknop. */
  designerOwnsOrder?: boolean;
}): ConfiguratorPrimaryAction {
  if (designerOwnsOrder && step !== 'checkout') return 'in-designer';
  if (step === 'configure') {
    if (!isPrintable) return 'add-to-cart';
    return designMadeInline && designsReady ? 'add-to-cart' : 'go-to-design';
  }
  if (step === 'upload') return designsReady ? 'add-to-cart' : 'await-design';
  return 'add-to-cart';
}

/**
 * Hoe de vinyl-ontwerptool op de productpagina staat.
 *
 * - `designer`: de Vinyl Lettering Designer is actief en zijn bestelknop is de
 *   enige (zie `in-designer`).
 * - `registered`: er staat al een geregistreerd ontwerp klaar (`?generatedUploadId=`);
 *   geen designer, de configurator bestelt zelf.
 * - `unavailable`: de designer kan hier niet bestellen. Bij een laadfout (bundel of
 *   JSON niet te laden), of als het product geen copies-optie heeft: dan prijzen
 *   host (aantal in `quantity`) en configurator niet dezelfde aanvraag, en zouden
 *   prijs en winkelwagenregel uit elkaar lopen. De klant krijgt een melding, bij een
 *   laadfout "Probeer opnieuw", en altijd een weg naar contact/offerte. Zonder
 *   ontwerp is er geen maat en geen bestand, dus de configurator kan dan zelf
 *   niets bestellen.
 * - `none`: geen vinyl-ontwerp (of beheer, alleen lezen).
 */
export type VinylDesignerMode = 'designer' | 'registered' | 'unavailable' | 'none';

export function resolveVinylDesignerMode({
  hasVinylDesigner,
  registeredDesign,
  readOnly,
  hasCopiesOption,
  loadFailed,
}: {
  hasVinylDesigner: boolean;
  registeredDesign: boolean;
  readOnly: boolean;
  hasCopiesOption: boolean;
  loadFailed: boolean;
}): VinylDesignerMode {
  if (!hasVinylDesigner || readOnly) return 'none';
  if (registeredDesign) return 'registered';
  if (!hasCopiesOption || loadFailed) return 'unavailable';
  return 'designer';
}

/**
 * Haalt de configurator de aantallen-/levertijdtabel (batchprijzen) op?
 *
 * Niet zolang de designer bestelt (`designer`) of onbeschikbaar is
 * (`unavailable`): die tabel is dan verborgen en de designer prijst zelf één
 * keer per ontwerpwijziging (Ruling P6). Anders ging er per wijziging nog een
 * batch van tien prijzen mee (gevonden in de e2e, Task 6).
 */
export function loadsDeliveryMatrix(mode: VinylDesignerMode): boolean {
  return mode !== 'designer' && mode !== 'unavailable';
}

/**
 * Wacht de configurator nog op de maat van de designer?
 *
 * Zolang de designer bestelt komt de maat (size_width/size_height bij size
 * 'custom') alleen uit de designer. Prijzen zonder maat geeft op de server een
 * fout ("Geen maat voor de plakletters", 500) en een consolefout bij elke
 * paginalading (gevonden in de e2e, Task 6); de designer meldt zijn maat direct
 * na het starten.
 */
export function awaitsDesignerSize(mode: VinylDesignerMode, configuration: Record<string, unknown>): boolean {
  if (mode !== 'designer' && mode !== 'unavailable') return false;
  if (String(configuration.size ?? '') !== 'custom') return false;
  return !(Number(configuration.size_width) > 0 && Number(configuration.size_height) > 0);
}

export type AddToCartBlock = 'upload-first' | null;

/**
 * Wat het toevoegen nog tegenhoudt aan de bestandskant. Een lage resolutie
 * hoort daar niet meer bij (oktober 2026): de klant ziet een rustige melding
 * en kan gewoon bestellen; de beheerder controleert het bestand.
 */
export function addToCartFileBlock({
  isPrintable,
  designCount,
  requiredDesignCount,
}: {
  isPrintable: boolean;
  designCount: number;
  requiredDesignCount: number;
}): AddToCartBlock {
  if (!isPrintable) return null;
  if (designCount < requiredDesignCount) return 'upload-first';
  return null;
}

const FLOW_COPY = {
  nl: {
    goToDesign: 'Ontwerp uploaden of maken',
    stepDesignAndOrder: 'Ontwerp & bestellen',
    awaitingDesign: 'Upload je bestand of maak je ontwerp hierboven. Daarna voeg je het direct toe aan je winkelwagen.',
  },
  en: {
    goToDesign: 'Upload or create your design',
    stepDesignAndOrder: 'Design & order',
    awaitingDesign: 'Upload your file or create your design above. Then add it straight to your cart.',
  },
  de: {
    goToDesign: 'Design hochladen oder gestalten',
    stepDesignAndOrder: 'Design & Bestellung',
    awaitingDesign: 'Laden Sie oben Ihre Datei hoch oder gestalten Sie Ihr Design. Danach legen Sie es direkt in den Warenkorb.',
  },
  fr: {
    goToDesign: 'Importer ou créer votre design',
    stepDesignAndOrder: 'Design et commande',
    awaitingDesign: 'Importez votre fichier ou créez votre design ci-dessus. Ajoutez-le ensuite directement au panier.',
  },
} as const;

export function configuratorFlowCopy(locale: string) {
  const lang = String(locale || 'nl').slice(0, 2) as keyof typeof FLOW_COPY;
  return FLOW_COPY[lang] || FLOW_COPY.nl;
}

type FileUploadSlotLike = { showIf?: unknown };

/**
 * Uploadvakken die bij de gekozen samenstelling horen. Een vak zonder `showIf`
 * hoort altijd bij het product; met `showIf` moet elke genoemde optie een van
 * de toegestane waarden hebben.
 */
export function visibleFileUploadSlots<T extends FileUploadSlotLike>(
  slots: T[] | null | undefined,
  configuration: Record<string, unknown>,
): T[] {
  if (!Array.isArray(slots)) return [];
  return slots.filter((slot) => {
    const showIf = slot?.showIf;
    if (!showIf || typeof showIf !== 'object') return true;
    return Object.entries(showIf as Record<string, unknown>).every(([optionId, allowedValues]) => {
      const selectedValue = String(configuration[optionId] ?? '');
      const accepted = Array.isArray(allowedValues) ? allowedValues : [allowedValues];
      return accepted.map(String).includes(selectedValue);
    });
  });
}

/**
 * Het product heeft uploadvakken, maar bij deze keuze hoort er geen een,
 * bijvoorbeeld de paalbanner als "Alleen houder". Dan is er niets te drukken
 * en hoort de klant direct naar de winkelwagen te kunnen. Zonder deze regel
 * viel het aantal ontwerpen terug op 1 en bleef de klant in de uploadstap
 * hangen zonder winkelwagenknop.
 */
export function uploadSlotsRuleOutPrintFile(
  slots: FileUploadSlotLike[] | null | undefined,
  configuration: Record<string, unknown>,
): boolean {
  if (!Array.isArray(slots) || slots.length === 0) return false;
  // Alleen beslissen op echte keuzes: zolang een optie uit de slotregels nog
  // leeg is (bijvoorbeeld voordat de standaardwaarden geladen zijn), blijft
  // het product een drukproduct.
  const referenced = new Set(slots.flatMap((slot) =>
    slot?.showIf && typeof slot.showIf === 'object' ? Object.keys(slot.showIf as Record<string, unknown>) : []));
  for (const optionId of referenced) {
    const value = configuration[optionId];
    if (value === undefined || value === null || value === '') return false;
  }
  return visibleFileUploadSlots(slots, configuration).length === 0;
}
